import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../AlertContext';
import { Container, Row, Col, Card, Tab, Nav, Table, Form, Button, Badge, Alert, Spinner, Modal, Tabs, Collapse } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faHistory, faTruck, faEdit, faMapMarkerAlt, faClock, faCheckCircle, faTimesCircle, faPlusCircle, faRefresh, faEye, faCog, faBell, faShield, faLifeRing, faQuestionCircle, faHeadset, faPhone, faEnvelope, faChevronUp, faChevronDown, faSignOutAlt, faLock, faKey, faSignInAlt, faDesktop, faMobile, faDownload, faTrash, faExclamationTriangle, faUtensils } from '@fortawesome/free-solid-svg-icons';
import './pages.css';
import { API } from '../config/api';
import getImageUrl from '../utils/getImageUrl';

const DashboardPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [activeTab, setActiveTab] = useState('orders');
  const { showAlert } = useAlert();
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [phoneError, setPhoneError] = useState('');
  const [addressForm, setAddressForm] = useState({ label: '', address: '', phone: '' });
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [hasFetchedOrders, setHasFetchedOrders] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [productImages, setProductImages] = useState({});
  const [faqItems, setFaqItems] = useState([
    {
      question: "How long does delivery take?",
      answer: "Delivery typically takes 45-60 minutes within Lagos metropolitan areas. Outside these areas, it may take up to 2 hours.",
      expanded: false
    },
    {
      question: "What are your payment options?",
      answer: "We accept cash on delivery, bank transfer, and card payments. All card payments are secure and encrypted.",
      expanded: false
    },
    {
      question: "Can I modify my order after placing it?",
      answer: "Orders can be modified within 10 minutes of placement. Please contact our support team immediately if your need changes.",
      expanded: false
    },
    {
      question: "Do you offer catering services?",
      answer: "Yes! We offer catering for events and corporate functions. Minimum order of ₦50,000 for catering services.",
      expanded: false
    },
    {
      question: "What are your operating hours?",
      answer: "We're open daily from 9:00 AM to 9:00 PM. Last orders for delivery are accepted until 8:30 PM.",
      expanded: false
    }
  ]);
  const [supportRequests, setSupportRequests] = useState([]);
  const [supportRequestsLoading, setSupportRequestsLoading] = useState(false);
  const [hasFetchedSupportRequests, setHasFetchedSupportRequests] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({
    subject: '',
    category: 'general',
    message: '',
    orderId: ''
  });
  const [selectedSupportRequest, setSelectedSupportRequest] = useState(null);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [showLoginActivityModal, setShowLoginActivityModal] = useState(false);
  const [loginActivity, setLoginActivity] = useState([]);
  const [loginActivityLoading, setLoginActivityLoading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await api.get('/auth/me');
        setUserData(response.data);
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      }
    };

    fetchUserData();
  }, [api]);
  
  // Fetch user orders from backend
  useEffect(() => {
    const fetchUserOrders = async () => {
      if (activeTab === 'orders' && userData && !hasFetchedOrders) {
        setOrdersLoading(true);
        try {
          console.log('Fetching user orders...');
          const response = await api.get('/orders/my-orders');
          setOrders(response.data);
          setHasFetchedOrders(true);
        } catch (error) {
          console.error("Failed to fetch user orders:", error);
          showAlert('Failed to load orders', 'danger');
        } finally {
          setOrdersLoading(false);
        }
      }
    };
    
    fetchUserOrders();
  }, [activeTab, api, showAlert, userData, hasFetchedOrders]);

  // Fetch product images when order modal is shown
  useEffect(() => {
    const fetchProductImages = async () => {
      if (showOrderModal && selectedOrder) {
        try {
          const imagePromises = selectedOrder.items.map(async (item) => {
            if (!item.image && item.product) {
              try {
                const response = await api.get(`/products/${item.product}`);
                return { productId: item.product, image: response.data.image };
              } catch (error) {
                console.error(`Failed to fetch image for product ${item.product}:`, error);
                return { productId: item.product, image: null };
              }
            }
            return null;
          });

          const images = await Promise.all(imagePromises);
          const imageMap = {};
          images.forEach(img => {
            if (img) imageMap[img.productId] = img.image;
          });
          
          setProductImages(imageMap);
        } catch (error) {
          console.error("Failed to fetch product images:", error);
        }
      }
    };

    fetchProductImages();
  }, [showOrderModal, selectedOrder, api]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'q') {
        e.preventDefault();
        setShowLogoutModal(true);
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'P') {
        e.preventDefault();
        setShowChangePasswordModal(true);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Fetch support requests
  useEffect(() => {
    const fetchSupportRequests = async () => {
      if (activeTab === 'support' && !hasFetchedSupportRequests) {
        setSupportRequestsLoading(true);
        try {
          const response = await api.get('/support-requests/my-requests');
          setSupportRequests(response.data);
          setHasFetchedSupportRequests(true); // Mark as fetched
        } catch (error) {
          console.error("Failed to fetch support requests:", error);
          showAlert('Failed to load support requests', 'danger');
        } finally {
          setSupportRequestsLoading(false);
        }
      }
    };
    
    fetchSupportRequests();
  }, [activeTab, api, showAlert, hasFetchedSupportRequests]);

  useEffect(() => {
    const fetchLoginActivity = async () => {
      if (showLoginActivityModal && loginActivity.length === 0) {
        setLoginActivityLoading(true);
        try {
          const response = await api.get('/auth/login-activity');
          setLoginActivity(response.data);
        } catch (error) {
          console.error('Error fetching login activity:', error);
          showAlert('Failed to load login activity', 'danger');
        } finally {
          setLoginActivityLoading(false);
        }
      }
    };

    fetchLoginActivity();
  }, [showLoginActivityModal, api, showAlert, loginActivity.length]);

  const refreshOrders = async () => {
    setOrdersLoading(true);
    try {
      console.log('Refreshing orders...');
      const response = await api.get('/orders/my-orders');
      setOrders(response.data);
      showAlert('Orders refreshed successfully', 'success');
    } catch (error) {
      console.error("Failed to refresh orders:", error);
      showAlert('Failed to refresh orders', 'danger');
    } finally {
      setOrdersLoading(false);
    }
  };

  // View order details
  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  // Close order modal
  const handleCloseOrderModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
  };

  // Get current order (most recent with status not 'delivered')
  const currentOrder = orders.find(order => order.status !== 'delivered') || null;

  const handlePhoneChange = (e) => {
    const phone = e.target.value;
    setAddressForm({ ...addressForm, phone });

    const isValid = /^0(70|80|81|90|91)[0-9]{8}$/.test(phone);
    setPhoneError(isValid ? '' : ''); // Invalid Nigerian phone number
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await api.post('/auth/upload-profile-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Update user data and image preview
      setUserData(prev => ({ ...prev, profileImage: response.data.imageUrl }));
      showAlert('Profile image updated!', 'success');
    } catch (err) {
      showAlert(err.response?.data?.message || 'Failed to upload image', 'danger');
    }
  };

  const handleAddressChange = (e) => {
    setAddressForm({ ...addressForm, [e.target.name]: e.target.value });
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();

    try {
      if (isEditingAddress) {
        const response = await api.put(`/auth/update-address/${editingIndex}`, addressForm);
        setUserData(prev => ({ ...prev, addresses: response.data.addresses }));
        showAlert('Address updated successfully!', 'success');
      } else {
        const response = await api.post('/auth/add-address', addressForm);
        setUserData(prev => ({ ...prev, addresses: response.data.addresses }));
        showAlert('Address added!', 'success');
      }
      setShowAddressModal(false);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save address';
      console.error("Add address error:", err);
      showAlert(msg, 'danger');
    }
  };

  const handleEditAddress = (index) => {
    const selected = userData.addresses[index];
    setAddressForm({ label: selected.label, address: selected.address, phone: selected.phone });
    setEditingIndex(index);
    setIsEditingAddress(true);
    setShowAddressModal(true);
  };

  const handleAddNewAddress = () => {
    setAddressForm({ label: '', address: '', phone: '' });
    setIsEditingAddress(false);
    setEditingIndex(null);
    setShowAddressModal(true);
  };

  const handleRemoveAddress = async (index) => {
    if (!window.confirm('Are you sure you want to remove this address?')) return;

    try {
      const response = await api.delete(`/auth/remove-address/${index}`);
      const updatedAddresses = response.data.addresses;

      // Check if the removed one was the default
      const wasDefault = userData.addresses[index]?.isDefault;

      const newDefaultAddress = wasDefault && updatedAddresses.length > 0
        ? updatedAddresses.find(addr => addr.isDefault) || updatedAddresses[0]
        : null;

      // Update userData fully
      setUserData(prev => ({
        ...prev,
        addresses: updatedAddresses,
        address: newDefaultAddress ? newDefaultAddress.address : ''
      }));
      showAlert('Address removed', 'info');
    } catch (err) {
      showAlert('Failed to remove address', 'danger');
    }
  };

  const setDefaultAddress = async (index) => {
    try {
      const response = await api.put('/auth/set-default-address', {
        addressIndex: index
      });

      setUserData(prev => ({
        ...prev,
        address: response.data.address, // update profile tab address
        addresses: prev.addresses.map((addr, i) => ({
          ...addr,
          isDefault: i === index
        }))
      }));

      showAlert('Default address updated', 'success');
    } catch (err) {
      showAlert('Failed to update default address', 'danger');
    }
  };

  const handleDownloadData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Please login again.");
        return;
      }

  const response = await fetch(`${API}/auth/download-data`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download PDF");
      }

      // Convert response to blob
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Create hidden download link
      const link = document.createElement("a");
      link.href = url;
      link.download = "my-data.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showAlert('Data downloaded successfully!', 'success');
    } catch (error) {
      console.error("Download failed:", error);
      setDownloadError('Could not download data. Please try again.');
      showAlert('Failed to download data', 'danger');
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'delivered':
        return <Badge bg="success"><FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Delivered</Badge>;
      case 'pending':
        return <Badge bg="warning"> Pending</Badge>;
      case 'confirmed':
        return <Badge bg="secondary"><FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Confirmed</Badge>
      case 'preparing':
        return <Badge bg="primary"><FontAwesomeIcon icon={faUtensils} className="me-1" /> Preparing</Badge>
      case 'cancelled':
        return <Badge bg="danger"><FontAwesomeIcon icon={faTimesCircle} className="me-1" /> Cancelled</Badge>;
      case 'out-for-delivery':
        return <Badge bg="info"><FontAwesomeIcon icon={faTruck} className="me-1" /> Out for delivery</Badge>;
      default:
        return <Badge bg="secondary"> {status}</Badge>;
    }
  };

  const refreshSupportRequests = async () => {
    setSupportRequestsLoading(true);
    try {
      const response = await api.get('/support-requests/my-requests');
      setSupportRequests(response.data);
      showAlert('Support requests refreshed', 'success');
    } catch (error) {
      console.error("Failed to refresh support requests:", error);
      showAlert('Failed to refresh support requests', 'danger');
    } finally {
      setSupportRequestsLoading(false);
    }
  };
    
  const viewSupportRequest = async (request) => {
    try {
      const response = await api.get(`/support-requests/${request._id}`);
      setSelectedSupportRequest(response.data);
      setShowSupportModal(true);
    } catch (error) {
      console.error("Failed to fetch support request details:", error);
      showAlert('Failed to load request details', 'danger');
    }
  };

  // Password verification
  const verifyPassword = async () => {
    if (!currentPassword) {
      setPasswordError('Please enter your current password');
      return;
    }

    setIsVerifying(true);
    setPasswordError('');

    try {
      // Verify the current password
      const response = await api.post('/auth/verify-password', {
        password: currentPassword
      });

      if (response.data.success) {
        // Password is correct, redirect to forgot password page
        setShowChangePasswordModal(false);
        setCurrentPassword('');
        
        // Store a flag in localStorage to indicate password verification passed
        localStorage.setItem('passwordVerified', 'true');
        localStorage.setItem('verifiedEmail', userData.email);
        
        // Redirect to forgot password page
        window.location.href = '/forgot-password';
      } else {
        setPasswordError('Incorrect password');
      }
    } catch (error) {
      console.error('Password verification error:', error);
      setPasswordError(error.response?.data?.message || 'Failed to verify password');
    } finally {
      setIsVerifying(false);
    }
  };

  // Get device icon
  const getDeviceIcon = (userAgent) => {
    if (userAgent?.includes('Mobile')) {
      return faMobile;
    }
    return faDesktop;
  };

  // Format date
  const formatLoginDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Function to format order tracking data
  const formatOrderTracking = (order) => {
    if (order.status === 'cancelled' || order.status === 'delivered') {
      return [];
    }

    const tracking = [];
    const statusHistory = order.statusHistory || {};
    
    // Define the order of status progression
    const statusOrder = ['ordered', 'confirmed', 'preparing', 'baking', 'out-for-delivery', 'delivered'];
    
    // Get the current status index
    const currentStatusIndex = statusOrder.indexOf(order.status);
    
    // Create tracking steps
    statusOrder.forEach((status, index) => {
      const isCompleted = status === 'ordered' ? true : index <= currentStatusIndex;
      let date;
      
      // Set dates based on status
      switch(status) {
        case 'ordered':
          date = order.createdAt;
          break;
        case 'confirmed':
          date = statusHistory.confirmed;
          break;
        case 'preparing':
          date = statusHistory.preparing;
          break;
        case 'baking':
          date = statusHistory.baking;
          break;
        case 'out-for-delivery':
          date = statusHistory.outForDelivery;
          break;
        case 'delivered':
          date = statusHistory.delivered;
          break;
        default:
          date = null;
      }
      
      tracking.push({
        status,
        date,
        completed: isCompleted
      });
    });
    
    return tracking;
  };

  const handleLogout = async () => {
    try {
      // Clear local storage
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');

      // Clear session storage as well
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('userData');

      // Show success message
      showAlert('Logged out successfully!', 'success');

      // Redirect to home or login page after a brief delay
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
      
    } catch (error) {
      console.error('Logout error:', error);
      showAlert('Error during logout. Please try again.', 'danger');
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  if (!userData) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="danger" />
        <p className="mt-3">Loading your dashboard...</p>
      </div>
    );
  }
  
  return (
    <div className="dashboard-page">
      <Container className="my-5">
        {/* Dashboard Header */}
        <Row className="mb-4">
          <Col>
            <h1 className="fw-bold">My Dashboard</h1>
            <p className="fs-4 text-muted">Welcome back, {userData.name}</p>
          </Col>
        </Row>

        {/* Main Dashboard Content */}
        <Row>
          <Col lg={3} className="mb-4">
            {/* User Profile Card */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="text-center">
                <div className="mb-3 text-center position-relative">
                  <div className="avatar-lg mx-auto bg-danger bg-opacity-10 text-danger rounded-circle d-flex align-items-center justify-content-center" style={{ width: "100px", height: "100px", fontSize: "2rem" }}>
                    {userData.profileImage ? (
                      <img
                        src={getImageUrl(userData.profileImage)} 
                        alt="Profile"
                        className="rounded-circle"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <FontAwesomeIcon icon={faUser} />
                    )}
                  </div>
                  
                  {/* Add Button */}
                  <input
                    type="file"
                    id="profile-upload"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleProfileImageUpload}
                  />

                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => document.getElementById('profile-upload').click()}
                  >
                    <FontAwesomeIcon icon={faPlusCircle} className="me-1" />
                    Add Photo
                  </Button>
                </div>
                <h5>{userData.name}</h5>
                <p className="text-muted small">{userData.email}</p>
                <p className="small">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1 text-danger" />
                  {userData.address || 'No default address yet'}
                </p>
                <p className="small text-muted">
                  Member since {userData.createdAt ? new Date(userData.createdAt).toLocaleDateString() : '—'}
                </p>
              </Card.Body>
            </Card>

            {/* Dashboard Navigation */}
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-0">
                <Nav variant="pills" className="flex-column">
                  <Nav.Item>
                    <Nav.Link 
                      active={activeTab === 'orders'} 
                      onClick={() => setActiveTab('orders')}
                      className="d-flex align-items-center"
                    >
                      <FontAwesomeIcon icon={faHistory} className="me-2" />
                      My Orders
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      active={activeTab === 'profile'} 
                      onClick={() => setActiveTab('profile')}
                      className="d-flex align-items-center"
                    >
                      <FontAwesomeIcon icon={faUser} className="me-2" />
                      My Profile
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      active={activeTab === 'address'} 
                      onClick={() => setActiveTab('address')}
                      className="d-flex align-items-center"
                    >
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                      Saved Addresses
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      active={activeTab === 'support'} 
                      onClick={() => setActiveTab('support')}
                      className="d-flex align-items-center"
                    >
                      <FontAwesomeIcon icon={faLifeRing} className="me-2" />
                      Support & Help
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      active={activeTab === 'settings'} 
                      onClick={() => setActiveTab('settings')}
                      className="d-flex align-items-center"
                    >
                      <FontAwesomeIcon icon={faCog} className="me-2" />
                      Settings
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      onClick={() => setShowLogoutModal(true)}
                      className="d-flex align-items-center text-danger"
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                      Logout
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={9}>
            {/* Tab Content */}
            <Tab.Content>
              {/* Orders Tab */}
              <Tab.Pane active={activeTab === 'orders'}>
                <Card className="border-0 shadow-sm mb-4">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h4 className="mb-0">
                        <FontAwesomeIcon icon={faHistory} className="me-2 text-danger" />
                        My Orders
                      </h4>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={refreshOrders}
                        disabled={ordersLoading}
                      >
                        <FontAwesomeIcon icon={faRefresh} className="me-1" />
                        Refresh
                      </Button>
                    </div>

                    {ordersLoading ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" variant="danger" />
                        <p className="mt-2">Loading your orders...</p>
                      </div>
                    ) : (
                      <>
                        {/* Current Order Tracking */}
                        {currentOrder && formatOrderTracking(currentOrder).length > 0 && (
                          <div className="mb-5">
                            <h5 className="mb-3">Current Order: {currentOrder._id.slice(-8).toUpperCase()}</h5>
                            <div className="tracking-steps">
                              {formatOrderTracking(currentOrder).map((step, index) => (
                                <div 
                                  key={index} 
                                  className={`tracking-step ${step.completed ? 'completed' : ''}`}
                                >
                                  <div className="step-indicator">
                                    {step.completed ? (
                                      <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                                    ) : (
                                      <div className="step-dot"></div>
                                    )}
                                  </div>
                                  <div className="step-details">
                                    <h6 className="text-capitalize mb-1">{step.status.replace(/-/g, ' ')}</h6>
                                    <p className="small text-muted mb-0">
                                      {step.date ? new Date(step.date).toLocaleString() : 'Pending'}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="mt-3">
                              <Button 
                                variant="outline-danger" 
                                size="sm" 
                                className="me-2"
                                onClick={() => viewOrderDetails(currentOrder)}
                              >
                                View Order Details
                              </Button>
                              <Button onClick={() => navigate('/contact')} variant="outline-secondary" size="sm">
                                Contact Support
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Order History */}
                        <h5 className="mb-3">Order History</h5>
                        {orders.length > 0 ? (
                          <div className="table-responsive">
                            {/* Desktop table - hidden on mobile */}
                            <Table hover className="mb-0 d-none d-md-table">
                              <thead>
                                <tr>
                                  <th>Order #</th>
                                  <th>Date</th>
                                  <th>Items</th>
                                  <th>Total</th>
                                  <th>Status</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {orders.map(order => (
                                  <tr key={order._id}>
                                    <td>...{order._id.slice(-8).toUpperCase()}</td>
                                    <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                                    <td>{order.items.length} item{order.items.length !== 1 ? 's' : ''}</td>
                                    <td>₦{order.total.toLocaleString()}</td>
                                    <td>{getStatusBadge(order.status)}</td>
                                    <td>
                                      <Button 
                                        variant="outline-danger" 
                                        size="sm"
                                        onClick={() => viewOrderDetails(order)}
                                      >
                                        <FontAwesomeIcon icon={faEye} /> Details
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>

                            {/* Mobile cards - shown only on mobile */}
                            <div className="d-md-none">
                              {orders.map(order => (
                                <Card key={order._id} className="mb-3 shadow-sm">
                                  <Card.Body>
                                    <div className="d-flex justify-content-between">
                                      <h6 className="fw-bold">Order #{order._id.slice(-8).toUpperCase()}</h6>
                                      {getStatusBadge(order.status)}
                                    </div>
                                    <div className="d-flex justify-content-between mt-2">
                                      <span>Date:</span>
                                      <span>{new Date(order.createdAt).toLocaleDateString()}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                      <span>Items:</span>
                                      <span>{order.items.length} item{order.items.length !== 1 ? 's' : ''}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                      <span>Total:</span>
                                      <span className="fw-bold">₦{order.total.toLocaleString()}</span>
                                    </div>
                                    <div className="d-grid gap-2 mt-3">
                                      <Button 
                                        variant="outline-danger" 
                                        size="sm"
                                        onClick={() => viewOrderDetails(order)}
                                      >
                                        <FontAwesomeIcon icon={faEye} className="me-1" />
                                        View Details
                                      </Button>
                                      <Button 
                                        as={Link} 
                                        to="/contact" 
                                        variant="outline-secondary" 
                                        size="sm"
                                      >
                                        Contact Support
                                      </Button>
                                    </div>
                                  </Card.Body>
                                </Card>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4 text-muted">
                            <FontAwesomeIcon icon={faHistory} size="3x" className="mb-3" />
                            <h5>No orders yet</h5>
                            <p>Your order history will appear here</p>
                            <Button onClick={() => handleNavigation('/menu')} variant="danger">
                              Browse Menu
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Profile Tab */}
              <Tab.Pane active={activeTab === 'profile'}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h4 className="mb-0">
                        <FontAwesomeIcon icon={faUser} className="me-2 text-danger" />
                        My Profile
                      </h4>
                    </div>

                    {successMessage && (
                      <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>
                        {successMessage}
                      </Alert>
                    )}

                    {errorMessage && (
                      <Alert variant="danger" onClose={() => setErrorMessage('')} dismissible>
                        {errorMessage}
                      </Alert>
                    )}

                    <div className="profile-details">
                      <Row className="mb-3">
                        <Col sm={4} className="fw-bold">Full Name:</Col>
                        <Col sm={8}>{userData.name}</Col>
                      </Row>
                      <Row className="mb-3">
                        <Col sm={4} className="fw-bold">Email:</Col>
                        <Col sm={8}>{userData.email}</Col>
                      </Row>
                      <Row className="mb-3">
                        <Col sm={4} className="fw-bold">Phone:</Col>
                        <Col sm={8}>{userData.phone || 'Not provided'}
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col sm={4} className="fw-bold">Address:</Col>
                        <Col sm={8}>{userData.address && userData.address.trim() !== '' 
                          ? userData.address 
                          : <span className="text-muted">No default address yet</span>}
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={4} className="fw-bold">Member Since:</Col>
                        <Col sm={8}>{userData.createdAt ? new Date(userData.createdAt).toLocaleDateString() : '—'}</Col>
                      </Row>
                    </div>
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Addresses Tab */}
              <Tab.Pane active={activeTab === 'address'}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h4 className="mb-0">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-danger" />
                        Saved Addresses
                      </h4>
                      <Button variant="outline-danger" size="sm" onClick={handleAddNewAddress}>
                        <FontAwesomeIcon icon={faEdit} className="me-1" /> Add New
                      </Button>
                    </div>

                    <Row>                      
                      {Array.isArray(userData.addresses) && userData.addresses.length > 0 ? (
                        userData.addresses.map((addr, index) => (
                          <Col md={6} className="mb-3" key={index}>
                            <Card className="h-100">
                              <Card.Body>
                                <div className="d-flex justify-content-between">
                                  <h5>{addr.label || 'Unnamed Address'}</h5>
                                  {addr.isDefault && <div className="text-danger">Default</div>}
                                </div>
                                <p className="mb-1">{addr.address}</p>
                                <p className="mb-1">Lagos, Nigeria</p>
                                <p className="mb-1">Phone: {addr.phone || ''}</p>
                                <div className="mt-3">
                                  <Button variant="outline-danger" size="sm" className="me-2" onClick={() => handleEditAddress(index)}>
                                    Edit
                                  </Button>
                                  <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => handleRemoveAddress(index)}>
                                    Remove
                                  </Button>
                                  {!addr.isDefault && (
                                    <Button variant="outline-success" size="sm" onClick={() => setDefaultAddress(index)}>
                                      Set Default
                                    </Button>
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))
                      ) : (
                        <p className="text-muted">No saved addresses yet.</p>
                      )}
                    </Row>
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Support & Help Tab */}
              <Tab.Pane active={activeTab === 'support'}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h4 className="mb-0">
                        <FontAwesomeIcon icon={faLifeRing} className="me-2 text-danger" />
                        Support & Help Center
                      </h4>
                      <Button variant="outline-danger" onClick={() => setShowContactModal(true)}>
                        <FontAwesomeIcon icon={faHeadset} className="me-1" />
                        Contact Support
                      </Button>
                    </div>

                    {/* Quick Contact Options */}
                    <Row className="mb-5">
                      <Col md={4} className="mb-3">
                        <Card className="text-center h-100 border-0 shadow-sm">
                          <Card.Body>
                            <FontAwesomeIcon icon={faPhone} size="2x" className="text-danger mb-3" />
                            <h5>Call Us</h5>
                            <p className="text-muted small">Speak directly with our support team</p>
                            <Button variant="outline-danger" target="_blank" size="sm" as="a" href="https://wa.me/2348032417124">
                              +234 803 241 7124
                            </Button>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={4} className="mb-3">
                        <Card className="text-center h-100 border-0 shadow-sm">
                          <Card.Body>
                            <FontAwesomeIcon icon={faEnvelope} size="2x" className="text-danger mb-3" />
                            <h5>Email Us</h5>
                            <p className="text-muted small">Get a response within 24 hours</p>
                            <Button variant="outline-danger" size="sm" as="a" href="mailto:support@noureeshfoods.com">
                              support@noureeshfoods.com
                            </Button>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={4} className="mb-3">
                        <Card className="text-center h-100 border-0 shadow-sm">
                          <Card.Body>
                            <FontAwesomeIcon icon={faClock} size="2x" className="text-danger mb-3" />
                            <h5>Live Chat</h5>
                            <p className="text-muted small">Available 8AM - 10PM daily</p>
                            <Button variant="outline-danger" size="sm" onClick={() => setShowContactModal(true)}>
                              Start Chat
                            </Button>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>

                    {/* FAQ Section */}
                    <div className="mb-5">
                      <h5 className="mb-3">
                        <FontAwesomeIcon icon={faQuestionCircle} className="me-2 text-danger" />
                        Frequently Asked Questions
                      </h5>
                      <div className="accordion" id="faqAccordion">
                        {faqItems.map((faq, index) => (
                          <Card key={index} className="mb-2">
                            <Card.Header 
                              className="bg-white"
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                const updatedFaqs = faqItems.map((item, i) => ({
                                  ...item,
                                  expanded: i === index ? !item.expanded : false
                                }));
                                setFaqItems(updatedFaqs);
                              }}
                            >
                              <h6 className="mb-0 d-flex justify-content-between align-items-center">
                                {faq.question}
                                <FontAwesomeIcon 
                                  icon={faq.expanded ? faChevronUp : faChevronDown} 
                                  className="text-muted"
                                />
                              </h6>
                            </Card.Header>
                            <Collapse in={faq.expanded}>
                              <div>
                                <Card.Body>
                                  <p className="mb-0">{faq.answer}</p>
                                </Card.Body>
                              </div>
                            </Collapse>
                          </Card>
                        ))}
                      </div>
                    </div>

                    {/* Support Request History */}
                    <div className="mb-5">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">
                          <FontAwesomeIcon icon={faQuestionCircle} className="me-2 text-danger" />
                          Support Request History
                        </h5>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={refreshSupportRequests}
                          disabled={supportRequestsLoading}
                          title="Refresh support requests"
                        >
                          <FontAwesomeIcon icon={faRefresh} className="me-1" />
                          Refresh
                        </Button>
                      </div>

                      {supportRequestsLoading ? (
                          <div className="text-center py-4">
                            <Spinner animation="border" variant="danger" />
                            <p className="mt-2">Loading support requests...</p>
                          </div>
                      ) : supportRequests.length > 0 ? (
                          <div className="table-responsive">
                            <Table hover>
                              <thead>
                                <tr>
                                  <th>Ticket #</th>
                                  <th>Subject</th>
                                  <th>Category</th>
                                  <th>Date</th>
                                  <th>Status</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {supportRequests.map((request) => (
                                  <tr key={request._id}>
                                    <td>#{request._id.slice(-8).toUpperCase()}</td>
                                    <td>{request.subject}</td>
                                    <td>
                                      <Badge bg="secondary" className="text-capitalize">
                                        {request.category}
                                      </Badge>
                                    </td>
                                    <td>{new Date(request.createdAt).toLocaleDateString()}</td>
                                    <td>
                                      <Badge bg={
                                        request.status === 'resolved' ? 'success' :
                                        request.status === 'in-progress' ? 'warning' : 
                                        request.status === 'closed' ? 'secondary' : 'primary'
                                      }>
                                        {request.status}
                                      </Badge>
                                    </td>
                                    <td>
                                      <Button
                                        variant="outline-info"
                                        size="sm"
                                        onClick={() => viewSupportRequest(request)}
                                        title="View Details"
                                      >
                                        <FontAwesomeIcon icon={faEye} /> Details
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                      ) : (
                          <div className="text-center py-4 text-muted">
                            <FontAwesomeIcon icon={faHistory} size="3x" className="mb-3" />
                            <h5>No support requests yet</h5>
                            <p>Your support ticket history will appear here</p>
                          </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Settings Tab */}
              <Tab.Pane active={activeTab === 'settings'}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <h4 className="mb-4">
                      <FontAwesomeIcon icon={faCog} className="me-2 text-danger" />
                      Account Settings
                    </h4>
                    
                    <Tabs defaultActiveKey="notifications" className="mb-4">
                      {/* Notification Settings */}
                      <Tab eventKey="notifications" title={
                        <span><FontAwesomeIcon icon={faBell} className="me-1" /> Notifications</span>
                      }>
                        <div className="mb-4">
                          <h5>Notification Preferences</h5>
                          <Form>
                            <Form.Check
                              type="switch"
                              id="email-notifications"
                              label="Email Notifications"
                              defaultChecked
                            />
                            <Form.Text className="text-muted">
                              Receive order updates and promotions via email
                            </Form.Text>
                          </Form>
                          {/* Add more settings here */}
                        </div>
                      </Tab>
                        
                      {/* Security Settings */}
                      <Tab eventKey="security" title={
                        <span><FontAwesomeIcon icon={faShield} className="me-1" /> Security</span>
                      }>
                        <div className="mb-4">
                          <h5>Security Settings</h5>
                          <Button 
                            variant="outline-danger" 
                            className="me-2 mb-2"
                            onClick={() => setShowChangePasswordModal(true)}
                          >
                            <FontAwesomeIcon icon={faKey} className="me-1" />
                            Change Password
                          </Button>
                          <Button 
                            variant="outline-secondary" 
                            className="me-2 mb-2"
                            onClick={() => setShowLoginActivityModal(true)}
                          >
                            <FontAwesomeIcon icon={faSignInAlt} className="me-1" />
                            View Login Activity
                          </Button>

                          <div className="mt-4">
                            <h6>Security Status</h6>
                            <div className="d-flex align-items-center mb-2">
                              <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                              <span>Email verified</span>
                            </div>
                            <div className="d-flex align-items-center mb-2">
                              <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                              <span>Password strength: Strong</span>
                            </div>
                          </div>
                        </div>
                      </Tab>

                      {/* Privacy Settings */}
                      <Tab eventKey="privacy" title={
                        <span><FontAwesomeIcon icon={faShield} className="me-1" /> Privacy</span>
                      }>
                        <div>
                          <h5>Privacy Settings</h5>
                          {/* Download My Data Button */}
                          <div className="mb-4 p-4 border rounded">
                            <div className="d-flex align-items-center mb-3">
                              <FontAwesomeIcon icon={faDownload} className="text-info me-3" size="lg" />
                              <div>
                                <h6 className="mb-1">Download Your Data</h6>
                                <p className="mb-0 text-muted">Get a copy of all your personal data stored in our systems.</p>
                              </div>
                            </div>
                            
                            <div className="mb-3">
                              <p className="small text-muted mb-2">Your download will include:</p>
                              <ul className="small text-muted">
                                <li>Profile information</li>
                                <li>Saved addresses</li>
                                <li>Login activity</li>
                              </ul>
                            </div>
                            
                            <Button 
                              variant="outline-info" 
                              onClick={handleDownloadData}
                              className="me-2"
                            >
                              <FontAwesomeIcon icon={faDownload} className="me-1" />
                              Download My Data
                            </Button>
                            
                            {downloadError && (
                              <Alert variant="danger" className="mt-3" onClose={() => setDownloadError('')} dismissible>
                                {downloadError}
                              </Alert>
                            )}
                          </div>
                          {/* Delete Account Button */}
                          <div className="p-4 border rounded">
                            <div className="d-flex align-items-center mb-3">
                              <FontAwesomeIcon icon={faTrash} className="text-danger me-3" size="lg" />
                              <div>
                                <h6 className="mb-1">Delete Your Account</h6>
                                <p className="mb-0 text-muted">Permanently delete your account and all associated data.</p>
                              </div>
                            </div>
                            
                            <div className="mb-3">
                              <p className="small text-muted mb-2">This action will permanently remove:</p>
                              <ul className="small text-muted">
                                <li>Your profile information</li>
                                <li>All order history</li>
                                <li>Saved addresses and preferences</li>
                                <li>Support request history</li>
                              </ul>
                              <p className="small text-danger">
                                <strong>Warning:</strong> This action cannot be undone. Please download your data first if you want to keep a record.
                              </p>
                            </div>
                            
                            <Button 
                              variant="outline-danger"
                              onClick={async () => {
                                if (window.confirm("Are you sure you want to delete your account? This action cannot be undone.")) {
                                  try {
                                    const token = localStorage.getItem("token");
                                    const res = await fetch(`${API}/auth/delete-account`, {
                                      method: "DELETE",
                                      headers: {
                                        "Authorization": `Bearer ${token}`
                                      }
                                    });

                                    const data = await res.json();
                                    if (!res.ok) {
                                      alert(data.message || "Failed to delete account");
                                      return;
                                    }

                                    alert("Your account has been deleted.");
                                    localStorage.removeItem("token");
                                    localStorage.removeItem("user");
                                    window.location.href = "/";
                                  } catch (err) {
                                    alert("An error occurred while deleting your account");
                                  }
                                }
                              }}
                            >
                              <FontAwesomeIcon icon={faTrash} className="me-1" />
                              Delete Account
                            </Button>
                          </div>
                        </div>
                      </Tab>
                    </Tabs>
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>

            {/* Order Details Modal */}
            <Modal show={showOrderModal} onHide={handleCloseOrderModal} size="lg" centered>
              <Modal.Header closeButton>
                <Modal.Title>Order Details: {selectedOrder?._id?.slice(-8)?.toUpperCase()}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {selectedOrder && (
                  <div>
                    {/* Order Status */}
                    <div className="mb-4">
                      <h5>Order Status: {getStatusBadge(selectedOrder.status)}</h5>
                      <p className="text-muted mb-0">
                        Ordered on: {new Date(selectedOrder.createdAt).toLocaleString()}
                      </p>
                    </div>

                    {/* Order Items */}
                    <div className="mb-4">
                      <h6>Items Ordered:</h6>
                      {selectedOrder.items.map((item, index) => (
                        <div key={index} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                          <div className="d-flex align-items-center">
                              {(item.image || productImages[item.product]) && (
                              <img 
                                src={item.image ? getImageUrl(item.image) : getImageUrl(productImages[item.product])} 
                                alt={item.name}
                                className="me-3"
                                style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px' }}
                                onError={(e) => {
                                  e.target.style.display = 'none'; // Hide broken images
                                }}
                              />
                            )}
                            <div>
                              <h6 className="mb-0">{item.name}</h6>
                              <small className="text-muted">₦{item.price.toLocaleString()} × {item.quantity}</small>
                            </div>
                          </div>
                          <div className="fw-bold">₦{(item.price * item.quantity).toLocaleString()}</div>
                        </div>
                      ))}
                    </div>

                    {/* Order Summary */}
                    <div className="mb-4">
                      <h6>Order Summary:</h6>
                      <div className="d-flex justify-content-between py-1">
                        <span>Subtotal:</span>
                        <span>₦{selectedOrder.total.toLocaleString()}</span>
                      </div>
                      <div className="d-flex justify-content-between py-1">
                        <span>Delivery Fee:</span>
                        <span>{selectedOrder.total > 10000 ? "Free" : "₦1,500"}</span>
                      </div>
                      <div className="d-flex justify-content-between py-2 border-top mt-2">
                        <strong>Total:</strong>
                        <strong>₦{selectedOrder.total.toLocaleString()}</strong>
                      </div>
                    </div>

                    {/* Delivery Information */}
                    <div className="mb-4">
                      <h6>Delivery Information:</h6>
                      <p><strong>Name:</strong> {selectedOrder.customerName}</p>
                      <p><strong>Phone:</strong> {selectedOrder.phone}</p>
                      <p><strong>Email:</strong> {selectedOrder.email}</p>
                      <p><strong>Address:</strong> {selectedOrder.shippingAddress}</p>
                      {selectedOrder.deliveryNotes && (
                        <p><strong>Delivery Notes:</strong> {selectedOrder.deliveryNotes}</p>
                      )}
                      {selectedOrder.scheduledDelivery && (
                        <Row className="mb-3">
                          <Col md={12}>
                            <h6>Scheduled Delivery</h6>
                            <p>
                              <strong>Date:</strong> {selectedOrder.scheduledDelivery.date || 'Not specified'}
                            </p>
                            <p>
                              <strong>Time:</strong> {selectedOrder.scheduledDelivery.time ? (
                                (() => {
                                  // Convert 24-hour format to 12-hour format with AM/PM
                                  const [hours, minutes] = selectedOrder.scheduledDelivery.time.split(':');
                                  const hourInt = parseInt(hours, 10);
                                  const period = hourInt >= 12 ? 'PM' : 'AM';
                                  const hour12 = hourInt % 12 || 12; // Convert 0 or 24 to 12
                                  return `${hour12}:${minutes || '00'} ${period}`;
                                })()
                              ) : 'Not specified'}
                            </p>
                          </Col>
                        </Row>
                      )}
                    </div>

                    {/* Payment Information */}
                    <div>
                      <h6>Payment Information:</h6>
                      <p><strong>Payment Method:</strong> {selectedOrder.paymentMethod}</p>
                      <p><strong>Payment Status:</strong> 
                        <Badge bg={selectedOrder.paymentStatus === 'paid' ? 'success' : 'warning'} className="ms-2">
                          {selectedOrder.paymentStatus}
                        </Badge>
                      </p>
                    </div>
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseOrderModal}>
                  Close
                </Button>
                <Button as={Link} to="/contact" variant="danger">
                  Contact Support
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Address Modal */}
            <Modal show={showAddressModal} onHide={() => setShowAddressModal(false)} centered>
              <Modal.Header closeButton>
                <Modal.Title>{isEditingAddress ? 'Edit Address' : 'Add New Address'}</Modal.Title>
              </Modal.Header>
              <Form onSubmit={handleAddressSubmit}>
                <Modal.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Label</Form.Label>
                    <Form.Control
                      type="text"
                      name="label"
                      placeholder="e.g., Home Address, Work Address"
                      value={addressForm.label}
                      onChange={handleAddressChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      type="text"
                      name="address"
                      placeholder="Enter your full address"
                      value={addressForm.address}
                      onChange={handleAddressChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      placeholder="Enter your Nigerian phone number"
                      value={addressForm.phone}
                      onChange={handlePhoneChange}
                      required
                    />
                    {phoneError && <Form.Text className="text-danger">{phoneError}</Form.Text>}
                  </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowAddressModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="danger">
                    {isEditingAddress ? 'Update Address' : 'Add Address'}
                  </Button>
                </Modal.Footer>
              </Form>
            </Modal>

            {/* Contact Support Modal */}
            <Modal show={showContactModal} onHide={() => setShowContactModal(false)} size="lg">
              <Modal.Header closeButton>
                <Modal.Title>Contact Support</Modal.Title>
              </Modal.Header>
              <Form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const response = await api.post('/support-requests', {
                    subject: contactForm.subject,
                    category: contactForm.category,
                    message: contactForm.message,
                    orderId: contactForm.orderId || null
                  });
                  
                  setSupportRequests(prev => [response.data.request, ...prev]);
                  setShowContactModal(false);
                  setContactForm({ subject: '', category: 'general', message: '', orderId: '' });
                  showAlert('Support request submitted successfully!', 'success');
                } catch (error) {
                  console.error("Failed to submit support request:", error);
                  showAlert('Failed to submit support request', 'danger');
                }
              }}>
                <Modal.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Subject *</Form.Label>
                        <Form.Control
                          type="text"
                          value={contactForm.subject}
                          onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                          required
                          placeholder="Brief description of your issue"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Category *</Form.Label>
                        <Form.Select
                          value={contactForm.category}
                          onChange={(e) => setContactForm({...contactForm, category: e.target.value})}
                          required
                        >
                          <option value="general">General Inquiry</option>
                          <option value="order">Order Issue</option>
                          <option value="delivery">Delivery Problem</option>
                          <option value="payment">Payment Issue</option>
                          <option value="quality">Quality Concern</option>
                          <option value="suggestion">Suggestion</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                      
                  <Form.Group className="mb-3">
                    <Form.Label>Related Order (optional)</Form.Label>
                    <Form.Select
                      value={contactForm.orderId}
                      onChange={(e) => setContactForm({...contactForm, orderId: e.target.value})}
                    >
                      <option value="">Select an order</option>
                      {orders.map(order => (
                        <option key={order._id} value={order._id}>
                          Order #{order._id.slice(-8)} - {new Date(order.createdAt).toLocaleDateString()}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Message *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      required
                      placeholder="Please describe your issue in detail..."
                    />
                  </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowContactModal(false)}>
                    Cancel
                  </Button>
                  <Button variant="danger" type="submit">
                    Submit Request
                  </Button>
                </Modal.Footer>
              </Form>
            </Modal>

            {/* Support Request Details Modal */}
            <Modal show={showSupportModal} onHide={() => setShowSupportModal(false)} size="lg">
              <Modal.Header closeButton>
                <Modal.Title>Support Request #{selectedSupportRequest?._id?.slice(-8).toUpperCase()}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {selectedSupportRequest && (
                  <>
                    <Row className="mb-3">
                      <Col md={6}>
                        <h6>Request Information</h6>
                        <p><strong>Subject:</strong> {selectedSupportRequest.subject}</p>
                        <p><strong>Category:</strong> {selectedSupportRequest.category}</p>
                        <p><strong>Status:</strong> 
                          <Badge bg={
                            selectedSupportRequest.status === 'resolved' ? 'success' :
                            selectedSupportRequest.status === 'in-progress' ? 'warning' : 'primary'
                          } className="ms-2">
                            {selectedSupportRequest.status}
                          </Badge>
                        </p>
                      </Col>
                      <Col md={6}>
                        <h6>Dates</h6>
                        <p><strong>Created:</strong> {new Date(selectedSupportRequest.createdAt).toLocaleString()}</p>
                        <p><strong>Last Updated:</strong> {new Date(selectedSupportRequest.updatedAt).toLocaleString()}</p>
                      </Col>
                    </Row>

                    <h6>Your Original Message</h6>
                    <div className="border p-3 mb-3 rounded bg-light">
                      {selectedSupportRequest.message}
                    </div>

                    <h6>Conversation</h6>
                    <div className="border rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {selectedSupportRequest.responses && selectedSupportRequest.responses.length > 0 ? (
                        selectedSupportRequest.responses.map((response, index) => (
                          <div key={index} className={`p-3 ${response.repliedBy.role === 'admin' ? 'bg-info bg-opacity-10' : 'bg-light'}`}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong>
                                {response.repliedBy.role === 'admin' ? 'Admin Support' : 'Admin Support'}
                                {response.repliedBy.name && ` (${response.repliedBy.name})`}
                              </strong>
                              <small className="text-muted">{new Date(response.createdAt).toLocaleString()}</small>
                            </div>
                            <p className="mb-0">{response.message}</p>
                          </div>
                        ))
                      ) : (
                        <div className="p-3 text-center text-muted">
                          No responses yet. Our support team will reply soon.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowSupportModal(false)}>
                  Close
                </Button>
                <Button 
                  variant="primary" 
                  onClick={() => {
                    setShowSupportModal(false);
                    setShowContactModal(true);
                    setContactForm(prev => ({
                      ...prev,
                      subject: `Re: ${selectedSupportRequest.subject}`,
                      orderId: selectedSupportRequest.orderId?._id || ''
                    }));
                  }}
                >
                  Add Reply
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Login Activity Modal */}
            <Modal show={showLoginActivityModal} onHide={() => setShowLoginActivityModal(false)} size="lg" centered>
              <Modal.Header closeButton>
                <Modal.Title>
                  <FontAwesomeIcon icon={faSignInAlt} className="me-2 text-danger" />
                  Login Activity
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {loginActivityLoading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" variant="danger" />
                    <p className="mt-2">Loading login activity...</p>
                  </div>
                ) : loginActivity.length > 0 ? (
                  <div className="login-activity-list">
                    {loginActivity.map((activity, index) => (
                      <div key={index} className="login-activity-item mb-3 p-3 border rounded">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="d-flex align-items-center">
                            <FontAwesomeIcon 
                              icon={getDeviceIcon(activity.userAgent)} 
                              className={`me-3 ${activity.success ? 'text-success' : 'text-danger'}`}
                              size="lg"
                            />
                            <div>
                              <h6 className="mb-1">
                                {activity.success ? 'Successful Login' : 'Failed Login Attempt'}
                              </h6>
                              <p className="mb-1 text-muted small">
                                <FontAwesomeIcon icon={faClock} className="me-1" />
                                {formatLoginDate(activity.timestamp)}
                              </p>
                              {activity.ipAddress && (
                                <p className="mb-1 text-muted small">
                                  <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                                  IP: {activity.ipAddress}
                                </p>
                              )}
                              {activity.location && (
                                <p className="mb-0 text-muted small">
                                  Location: {activity.location}
                                </p>
                              )}
                            </div>
                          </div>
                          <Badge bg={activity.success ? 'success' : 'danger'}>
                            {activity.success ? 'Success' : 'Failed'}
                          </Badge>
                        </div>
                        {activity.userAgent && (
                          <p className="mb-0 mt-2 small text-muted">
                            Device: {activity.userAgent}
                          </p>
                        )}
                        {!activity.success && (
                          <p className="mb-0 mt-2 small text-warning">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                            Invalid credentials provided
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <FontAwesomeIcon icon={faSignInAlt} size="3x" className="mb-3" />
                    <h5>No login activity found</h5>
                    <p>Your login history will appear here</p>
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowLoginActivityModal(false)}>
                  Close
                </Button>
                <Button 
                  variant="outline-danger" 
                  onClick={() => {
                    const fetchLoginActivity = async () => {
                      setLoginActivityLoading(true);
                      try {
                        const response = await api.get('/auth/login-activity');
                        setLoginActivity(response.data);
                      } catch (error) {
                        console.error('Error fetching login activity:', error);
                        showAlert('Failed to load login activity', 'danger');
                      } finally {
                        setLoginActivityLoading(false);
                      }
                    };
                    fetchLoginActivity();
                  }} 
                  disabled={loginActivityLoading}
                >
                  <FontAwesomeIcon icon={faRefresh} className="me-1" />
                  Refresh
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Logout Confirmation Modal */}
            <Modal show={showLogoutModal} onHide={() => setShowLogoutModal(false)} centered>
              <Modal.Header closeButton>
                <Modal.Title>Confirm Logout</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="text-center">
                  <FontAwesomeIcon icon={faSignOutAlt} size="3x" className="text-danger mb-3" />
                  <h5>Are you sure you want to logout?</h5>
                  <p className="text-muted">
                    You'll need to login again to access your account and place orders.
                  </p>
                </div>
              </Modal.Body>
              <Modal.Footer className="justify-content-center">
                <Button 
                  variant="outline-secondary" 
                  onClick={() => setShowLogoutModal(false)}
                  style={{ minWidth: '100px' }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="danger" 
                  onClick={handleLogout}
                  style={{ minWidth: '100px' }}
                >
                  Logout
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Change Password Modal */}
            <Modal show={showChangePasswordModal} onHide={() => setShowChangePasswordModal(false)} centered>
              <Modal.Header closeButton>
                <Modal.Title>
                  <FontAwesomeIcon icon={faKey} className="me-2 text-danger" />
                  Change Password
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="text-center mb-3">
                  <FontAwesomeIcon icon={faLock} size="3x" className="text-danger mb-2" />
                  <p>Please enter your current password to continue</p>
                </div>
                
                <Form.Group className="mb-3">
                  <Form.Label>Current Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={currentPassword}
                    onChange={(e) => {
                      setCurrentPassword(e.target.value);
                      setPasswordError('');
                    }}
                    placeholder="Enter your current password"
                    isInvalid={!!passwordError}
                    disabled={isVerifying}
                  />
                  {passwordError && (
                    <Form.Text className="text-danger">{passwordError}</Form.Text>
                  )}
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button 
                  variant="secondary" 
                  onClick={() => {
                    setShowChangePasswordModal(false);
                    setCurrentPassword('');
                    setPasswordError('');
                  }}
                  disabled={isVerifying}
                >
                  Cancel
                </Button>
                <Button 
                  variant="danger" 
                  onClick={verifyPassword}
                  disabled={isVerifying}
                >
                  {isVerifying ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-1"
                      />
                      Verifying...
                    </>
                  ) : 'Continue'}
                </Button>
              </Modal.Footer>
            </Modal>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DashboardPage;